import { AutoReportMode, Language, ReminderEmail } from 'app/shared/models';
import {
  CompanyProductDefinition,
  EmailListPreview, GroupCategoriesHierarchy, GroupResultInfo, ProductIndex, UserView,
  WizardSelectionGroup
} from '.';
import { ModuleView } from '../product-wizard/survey/module-selection/models';
import { ValidationData } from '../product-wizard/survey/module-selection/models/survey-wizard-question.model';
import { SmsList } from './sms.model';

export enum WizardTypes {
  survey = 'survey',
  followup = 'followup'
}

export type WizardContexts = SurveyWizardContext | FollowupWizardContext;

export interface ProductWizardContext {
  activeStep: number;
  productId?: number;
  unsaved: boolean;
  saving: boolean;
  productDefinition: CompanyProductDefinition;
  currentVersion: string;
  pristine?: boolean;
}

export interface SurveyWizardContext extends ProductWizardContext {
  wizardType: WizardTypes.survey;
  modules: ModuleView[];
  allModules: ModuleView[];
  participants: ParticipantContext;
  schedule: ScheduleContext;
  communication: CommunicationContext;
  name: string;
  index?: IndexContext;
  surveyId?: number;
  moduleId?: number;
  wasDeactivated: boolean;
  editMode: boolean;
  editedSurveyId?: number;
  isPaused?: boolean;
}

export interface RecurringEditTarget {
  surveyId: number;
  isPaused: boolean;
  schedule: Partial<ScheduleContext>;
}

export const recurringTargetId = (context: SurveyWizardContext) =>
  context.editedSurveyId ?? context.surveyId;

export interface FollowupWizardContext extends ProductWizardContext {
  groupResultInfo: GroupResultInfo;
  groupId: number;
  actionAreaKey: string;
  selectedCategory?: RecommendationCategory;
  selectedSubCategory?: RecommendationSubCategory;
  selectedRecommendation: Recommendation;
  wizardType: WizardTypes.followup;
  nows: FollowupNow[];
  goals: FollowupGoal[];
  actions: FollowupAction[];
  recommendations: Recommendation[];
  suggestions?: string;
  getSuggestions?: boolean;
  byPassGuard?: boolean;
}

export interface ScheduleContext {
  startDate: Date;
  endDate: Date;
  reportDate: Date;
  directActivation: boolean;
  reminderEmails: ReminderEmail[];
  cronExpression?: string;
  intervalUnit?: 'Week' | 'Month' | 'Day';
  intervalValue?: number;
  duration: string;
  reporting: string;
  timeZone?: string;
  generate?: boolean;
}

export interface CommunicationContext {
  activationEmail: EmailListPreview;
  activationSms: SmsList;
  autoReport: boolean;
  autoReportMode: AutoReportMode;
  communicationEnabled: boolean;
  communicationMethod: string;
  hasPinGroups: boolean;
  pinCodeEmail: EmailListPreview;
  pinCodeSms: SmsList;
  previewLanguage: Language;
  reminderEmail?: EmailListPreview;
  reportEmail: EmailListPreview;
  welcomeSms: SmsList;
}

export interface IndexContext {
  indexInfo: ProductIndex[];
  indexValidation: ValidationData[];
  indexes: number[];
}
export interface ParticipantContext {
  selectedHierarchy: GroupCategoriesHierarchy;
  groups: WizardSelectionGroup[];
  participantCount: number;
  phoneNumberParticipantCount: number;
  nonPinCodeParticipantCount: number;
  emailParticipantCount: number;
  perspectivesRespondentsCount?: number;
  perspectivesFocusUsersCount?: number;
}

export interface FollowupAction {
  id: number;
  tempId?: string; // to enable editing for not saved items (without proper id)
  productId: number;
  groupId?: number;
  title?: string;
  description?: string;
  deadline?: Date;
  status: ActionStatus;
  responsibleUsers?: UserView[];
  responsibleUsersNames?: string;
  recommendationId?: number;
  edit: boolean;
}

export enum ActionStatus {
  workinprogress = 'WorkInProgress',
  completed = 'Completed',
  cancelled = 'Cancelled'
}

export interface FollowupActionAdd {
  title?: string;
  description?: string;
  deadline?: Date;
  productId?: number;
  groupId?: number;
  responsibleUserIds?: number[];
  recommendationId?: number;
}
export interface FollowupActionEdit {
  id: number;
  title?: string;
  description?: string;
  deadline?: Date;
  groupId?: number;
  responsibleUserIds?: number[];
  recommendationId?: number;
  status: string;
}

export interface FollowupNow {
  description: string;
  edit: boolean;
  tempId: string;
}

export interface FollowupGoal {
  description: string;
  edit: boolean;
  tempId: string;
}
export interface Recommendation {
  id?: number;
  categoryKey?: string;
  category?: string;
  subCategoryKey?: string;
  subCategory?: string;
  name?: string;
  shortText?: string;
  text?: string;
  key?: string;
  languageId?: number;
}

export interface ActionArea {
  id: number;
  key: string;
  name: string;
  useInProductContext?: string[];
}
export interface RecommendationCategory {
  key: string;
  name: string;
}

export interface RecommendationSubCategory {
  categoryKey?: string;
  subCategoryKey?: string;
  name?: string;
}

export function isSurveyWizardContext(context: WizardContexts): context is SurveyWizardContext {
  return context && 'modules' in context;
}
export function isFolloupWizardContext(context: WizardContexts): context is SurveyWizardContext {
  return context && 'recommendations' in context;
}
